/**
 * The write half of the funnel return params (see funnelReturnParams.ts for
 * what they are and how a page reads them). It ships in the entry chunk, next
 * to the boot-time scrub that calls it, so it stays tiny and has no imports.
 * Pages import everything through funnelReturnParams.ts.
 */
export const FUNNEL_RETURN_PARAMS_STORAGE_KEY = 'jp:funnel-return-params';
/** The one-hour localStorage copy that a new tab can pick up. */
export const FUNNEL_RETURN_PARAMS_HANDOFF_KEY = 'jp:funnel-return-params:handoff';

// Stripe Checkout Session ids start with `cs_` (cs_live_..., cs_test_...).
const CHECKOUT_SESSION_ID_PATTERN = /^cs_[A-Za-z0-9_]+$/;

/**
 * A real Stripe Checkout Session id, trimmed, or ''. Anything else is dropped,
 * above all an unfilled `{CHECKOUT_SESSION_ID}` template: every buyer would
 * share it as an event and transaction id, and it never matches the server's
 * event for the purchase.
 */
export function validCheckoutSessionId(value: unknown): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return CHECKOUT_SESSION_ID_PATTERN.test(trimmed) ? trimmed : '';
}

/** A pathname without query, hash or trailing slashes, for comparing pages. */
export function normalizeFunnelPath(path: string): string {
  return path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
}

// The newest set when sessionStorage would not take it (blocked or full).
// The scrub has already taken the references out of the URL, so without this
// copy the page would lose them. It lasts until the page reloads, and a later
// set that sessionStorage does take replaces it.
let unsavedReturn: string | null = null;

/** The serialized set sessionStorage refused, or null. */
export function readUnsavedFunnelReturn(): string | null {
  return unsavedReturn;
}

/** Tests only: start the next test like a fresh page load. */
export function forgetUnsavedFunnelReturn(): void {
  unsavedReturn = null;
}

/**
 * Saves the return params a scrub removed from the URL (this tab, plus the
 * one-hour copy a new tab can pick up). `path` is the pathname of the page
 * that reads them (see readStoredFunnelReturnParamsForPage). Replaces the
 * previous set; an empty set changes nothing. When sessionStorage refuses the
 * set, it is kept in memory for this page load instead.
 */
export function storeFunnelReturnParams(
  params: Record<string, string>,
  now: number = Date.now(),
  path?: string,
): void {
  const clean: Record<string, string> = {};
  for (const [name, value] of Object.entries(params)) {
    if (typeof value === 'string' && value.trim()) clean[name] = value.trim();
  }
  if (Object.keys(clean).length === 0) return;
  const serialized = JSON.stringify({
    savedAt: now,
    params: clean,
    ...(path ? { path: normalizeFunnelPath(path) } : {}),
  });
  try {
    window.sessionStorage.setItem(FUNNEL_RETURN_PARAMS_STORAGE_KEY, serialized);
    unsavedReturn = null;
  } catch {
    // Storage full or blocked. sessionStorage may still hold an older set, so
    // readers check this copy first.
    unsavedReturn = serialized;
  }
  try {
    window.localStorage.setItem(FUNNEL_RETURN_PARAMS_HANDOFF_KEY, serialized);
  } catch {
    // No hand-off to another tab.
  }
}
